import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import GeoSensitiveSlideshow from '../components/GeoSensitiveSlideshow';
import { AvatarDropdown } from '../components/AvatarDropdown';
import { showToast } from '../utils/toast';

type TransferFile = {
  id: string;
  name: string;
  sizeBytes: number | null;
  mime?: string | null;
};

type TransferMeta = {
  senderEmail: string;
  recipientEmail: string;
  message: string | null;
  createdAt: string;
  expiresAt?: string | null;
};

const formatExpiry = (expiresAt?: string | null): string | null => {
  if (!expiresAt) return null;
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'Expired';
  const hours = Math.floor(ms / (1000 * 60 * 60));
  const days = Math.floor(hours / 24);
  if (days >= 2) return `Expires in ${days} days`;
  if (hours >= 1) return `Expires in ${hours} hour${hours !== 1 ? 's' : ''}`;
  const mins = Math.floor(ms / (1000 * 60));
  return `Expires in ${Math.max(1, mins)} minute${mins !== 1 ? 's' : ''}`;
};

const formatBytes = (bytes: number | null) => {
  if (!Number.isFinite(bytes || 0) || !bytes) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const idx = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, idx);
  return `${value.toFixed(value >= 10 || idx === 0 ? 0 : 1)} ${units[idx]}`;
};

const HERO_MESSAGES = [
  {
    eyebrow: 'PRIVATE BY DESIGN',
    headline: 'Not Everything Belongs\non Social Media.',
    sub: 'Build your private digital home instead.',
    body: 'Your Memories. Your Documents. Your Stories.\nAll in one secure place — protected today, remembered forever.',
  },
  {
    eyebrow: 'DIGITAL LEGACY',
    headline: 'Your Files Reach\nthe Right Hands.',
    sub: 'Automatically, when the time comes.',
    body: 'Designate successors. Set conditions. Rest easy — your most important files are never lost.',
  },
  {
    eyebrow: 'END-TO-END ENCRYPTED',
    headline: 'Only You\nHold the Keys.',
    sub: 'True zero-knowledge encryption.',
    body: 'Not us. Not anyone else. Just you — and the people you choose to share with.',
  },
];

const Download: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [transfer, setTransfer] = useState<TransferMeta | null>(null);
  const [files, setFiles] = useState<TransferFile[]>([]);
  const [heroMsgIdx, setHeroMsgIdx] = useState(0);
  const [linkCopied, setLinkCopied] = useState(false);
  const [limitReached, setLimitReached] = useState(false);

  // Password gate
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordChecking, setPasswordChecking] = useState(false);
  const [unlockedPassword, setUnlockedPassword] = useState('');

  // Sort + selection state (OneDrive-inspired)
  const [sortBy, setSortBy] = useState<'name' | 'size'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const sortedFiles = useMemo(() => {
    return [...files].sort((a, b) => {
      let cmp = 0;
      if (sortBy === 'name') cmp = a.name.localeCompare(b.name);
      else cmp = (Number(a.sizeBytes) || 0) - (Number(b.sizeBytes) || 0);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [files, sortBy, sortDir]);

  const toggleSort = useCallback((col: 'name' | 'size') => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  }, [sortBy]);

  const allSelected = selectedIds.size === files.length && files.length > 0;
  const toggleAll = () => setSelectedIds(allSelected ? new Set() : new Set(files.map(f => f.id)));
  const toggleFile = (id: string) => setSelectedIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  // Header state
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check if user is logged in
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch('/api/me', { credentials: 'include' });
        const j = await r.json().catch(() => ({}));
        if (j?.user?.email) {
          setIsLoggedIn(true);
        }
      } catch {}
    })();
  }, []);

  const handleLogout = async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (error) {
    } finally {
      setIsLoggedIn(false);
    }
  };

  useEffect(() => {
    if (!id) return;
    let mounted = true;

    const load = async (pw?: string) => {
      setLoading(true);
      setError(null);
      try {
        const pwParam = pw ? `&password=${encodeURIComponent(pw)}` : '';
        const res = await fetch(`/api/transfers/public?token=${encodeURIComponent(id)}${pwParam}`);
        const data = await res.json().catch(() => ({}));
        if (!mounted) return;
        // Password required
        if (data?.passwordRequired && !data?.ok === false) {
          setPasswordRequired(true);
          setLoading(false);
          return;
        }
        if (!res.ok || data?.ok === false) throw new Error(data?.error || 'Transfer not found');
        setPasswordRequired(false);
        setLimitReached(!!data.limitReached);
        setTransfer(data.transfer || null);
        setFiles(Array.isArray(data.files) ? data.files : []);

        await fetch('/api/transfers/track/view', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: id }),
        }).catch(() => null);
      } catch (err: any) {
        if (!mounted) return;
        setError(String(err?.message || err));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    void load();
    return () => { mounted = false; };
  }, [id]);

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordInput.trim()) return;
    setPasswordChecking(true);
    setPasswordError('');
    try {
      const res = await fetch(`/api/transfers/public?token=${encodeURIComponent(id!)}&password=${encodeURIComponent(passwordInput)}`);
      const data = await res.json().catch(() => ({}));
      if (res.status === 401 || data?.passwordRequired) {
        setPasswordError('Incorrect password. Please try again.');
        return;
      }
      if (!res.ok || data?.ok === false) {
        setPasswordError(data?.error || 'Failed to unlock transfer.');
        return;
      }
      // Success
      setUnlockedPassword(passwordInput);
      setPasswordRequired(false);
      setLimitReached(!!data.limitReached);
      setTransfer(data.transfer || null);
      setFiles(Array.isArray(data.files) ? data.files : []);
    } catch {
      setPasswordError('Something went wrong. Please try again.');
    } finally {
      setPasswordChecking(false);
    }
  };

  const totalBytes = useMemo(() => files.reduce((sum, f) => sum + Number(f.sizeBytes || 0), 0), [files]);

  const downloadTransferFile = async (file: TransferFile) => {
    if (!id) return;
    const pwParam = unlockedPassword ? `&password=${encodeURIComponent(unlockedPassword)}` : '';
    const res = await fetch(
      `/api/transfers/download?token=${encodeURIComponent(id)}&fileId=${encodeURIComponent(file.id)}${pwParam}`,
      { credentials: 'omit' }
    );
    if (!res.ok) {
      const payload = await res.json().catch(() => ({} as any));
      throw new Error(String(payload?.error || `Download failed (${res.status})`));
    }
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = file.name || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(blobUrl);
  };

  const handleDownload = async (overrideFiles?: TransferFile[]) => {
    const targets = overrideFiles
      ?? (selectedIds.size > 0 ? files.filter(f => selectedIds.has(f.id)) : files);
    if (!targets.length || !id || isDownloading) return;
    setIsDownloading(true);
    setDownloadProgress(0);

    try {
      for (let idx = 0; idx < targets.length; idx += 1) {
        await downloadTransferFile(targets[idx]);
        setDownloadProgress(Math.round(((idx + 1) / targets.length) * 100));
      }
    } catch (err: any) {
      showToast(String(err?.message || 'Failed to download files'), 'error');
    } finally {
      setTimeout(() => setIsDownloading(false), 250);
    }
  };

  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Cycle hero messages
  useEffect(() => {
    const t = setInterval(() => setHeroMsgIdx(p => (p + 1) % HERO_MESSAGES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const mobileTickerText = 'Not Everything Belongs on Social Media. Build your private digital home instead.';

  const handleSaveToDrive = async () => {
    if (!id || !isLoggedIn) return;
    
    setIsSaving(true);
    try {
      const r = await fetch('/api/transfers/save-to-drive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token: id }),
      });
      const j = await r.json().catch(() => ({}));
      
      if (!r.ok || j?.ok === false) {
        const errorMsg = j?.details ? `${j.error}: ${j.details}` : (j?.error || 'Failed to save files');
        throw new Error(errorMsg);
      }
      
      setSaveSuccess(true);
      
      // Show success message with count
      if (j?.savedCount && j?.totalCount && j.savedCount < j.totalCount) {
        showToast(`Saved ${j.savedCount} of ${j.totalCount} files to My Drive`, 'success');
      }
      
      setTimeout(() => {
        navigate('/drive');
      }, 1500);
    } catch (err: any) {
      showToast(`Failed to save files: ${String(err?.message || err)}`, 'error', 4200);
    } finally {
      setIsSaving(false);
    }
  };

  const fileIcon = (file: TransferFile) => {
    const name = file.name.toLowerCase();
    if (name.endsWith('.pdf')) return 'picture_as_pdf';
    if (name.endsWith('.xls') || name.endsWith('.xlsx') || name.endsWith('.csv')) return 'table_view';
    if (name.endsWith('.mp4') || name.endsWith('.mov') || name.endsWith('.mkv')) return 'movie';
    if (name.endsWith('.zip') || name.endsWith('.rar')) return 'folder_zip';
    if (name.endsWith('.png') || name.endsWith('.jpg') || name.endsWith('.jpeg') || name.endsWith('.webp') || name.endsWith('.gif') || name.endsWith('.heic')) return 'image';
    return 'insert_drive_file';
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans relative">
      <style>{`@keyframes mobileMarquee{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}`}</style>
      {/* Desktop Header - Same as Landing Page */}
      <header className="hidden md:flex items-center justify-between whitespace-nowrap px-6 py-4 md:px-10 absolute top-0 left-0 right-0 z-50">
        <div className="flex items-center gap-3 text-[#0d121b] cursor-pointer group" onClick={() => navigate('/')}>
          <img
            src="/brand/wesaves-logo.png"
            alt="Wesaves"
            className="h-7 md:h-8 w-auto select-none brightness-0 md:invert md:drop-shadow-lg"
            draggable={false}
          />
        </div>
        <div className="flex flex-1 justify-end gap-4 items-center">
          <nav className="flex items-center gap-8 bg-white rounded-full px-8 py-3 shadow-lg shadow-black/5">
            {isLoggedIn && (
              <>
                <button onClick={() => navigate('/dashboard')} className="text-sm font-bold text-slate-500 hover:text-primary transition-colors">
                  Home
                </button>
                <button onClick={() => navigate('/drive')} className="text-sm font-bold text-slate-500 hover:text-primary transition-colors">
                  My Drive
                </button>
              </>
            )}
            <button onClick={() => navigate('/whats-new')} className="text-gray-900 hover:text-primary transition-colors text-sm font-bold">What's New</button>
            <button onClick={() => navigate('/pricing')} className="text-gray-900 hover:text-primary transition-colors text-sm font-bold">Pricing</button>
          </nav>
          <div className="flex items-center gap-2 bg-white rounded-full p-1.5 pl-6 shadow-lg shadow-black/5 relative">
            <AvatarDropdown isLoggedIn={isLoggedIn} onLogout={handleLogout} />
          </div>
        </div>
      </header>

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-50 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate('/')}
          >
            <img src="/brand/wesaves-logo.png" alt="Wesaves" className="h-6 w-auto" />
          </div>
          <button
            onClick={() => navigate('/')}
            className="p-2 rounded-full hover:bg-slate-100 transition-colors"
          >
            <span className="material-symbols-outlined text-slate-600">home</span>
          </button>
        </div>
      </header>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col items-start justify-center px-0 py-6 md:py-0 relative overflow-y-auto">
        {/* Background Slideshow */}
        <div className="absolute inset-0 overflow-hidden will-change-transform">
          <GeoSensitiveSlideshow 
            apiKey="G9UPa4SXNnv2eirQ1c6s9W81xm345WagePkhJx2Jzy8"
            interval={10000}
          />
        </div>
        
        <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-20 will-change-auto">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] aspect-square bg-primary/20 rounded-full blur-[120px]"></div>
          <div className="absolute bottom-[-10%] right-[-10%] w-[40%] aspect-square bg-blue-400/20 rounded-full blur-[120px]"></div>
        </div>

        {/* Marketing Hero Copy - Desktop — cycling */}
        <div className="hidden md:flex absolute top-0 bottom-0 right-0 w-[48%] items-center z-10 px-12 lg:px-20 text-left">
          <div className="relative w-full" style={{ minHeight: '260px' }}>
            {HERO_MESSAGES.map((msg, i) => (
              <div
                key={i}
                className={`absolute inset-0 flex flex-col justify-center transition-all duration-1000 ${
                  i === heroMsgIdx ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6 pointer-events-none'
                }`}
                style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4), 0 4px 20px rgba(0,0,0,0.3)' }}
              >
                <p className="text-[11px] font-black tracking-[0.25em] uppercase text-slate-200 mb-4">
                  {msg.eyebrow}
                </p>
                <h2 className="font-serif tracking-tight leading-[1.1] mb-4">
                  <span className="block text-3xl lg:text-4xl font-medium text-white whitespace-pre-line">
                    {msg.headline}
                  </span>
                  <span className="block text-xl lg:text-2xl font-medium text-slate-200 mt-2">
                    {msg.sub}
                  </span>
                </h2>
                <p className="text-sm lg:text-base text-slate-200 font-light leading-relaxed max-w-md whitespace-pre-line">
                  {msg.body}
                </p>
              </div>
            ))}
            {/* Dot indicators */}
            <div className="absolute bottom-0 left-0 flex gap-1.5">
              {HERO_MESSAGES.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setHeroMsgIdx(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === heroMsgIdx ? 'w-6 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Marketing Hero Copy - Mobile */}
        <div className="md:hidden relative z-10 w-full px-4 py-6 text-center bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent">
          <div className="text-white" style={{ textShadow: '0 2px 8px rgba(0,0,0,0.5)' }}>
            <p className="text-[9px] font-black tracking-[0.2em] uppercase text-slate-200 mb-2">
              PRIVATE BY DESIGN
            </p>
            <div className="overflow-hidden whitespace-nowrap">
              <div className="inline-flex min-w-full text-sm font-semibold text-slate-100 [animation:mobileMarquee_14s_linear_infinite]">
                <span className="pr-8">{mobileTickerText}</span>
                <span className="pr-8">{mobileTickerText}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="w-[calc(100%-2rem)] max-w-md bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl shadow-blue-500/5 p-3 md:p-6 relative z-10 animate-in zoom-in-95 duration-150 mx-4 md:ml-12 lg:ml-20 md:mr-0 md:w-full md:max-w-lg">
          <div className="flex flex-col items-start text-left mb-4 md:mb-6">
            {transfer && (
              <div className="flex flex-wrap items-center gap-2 mb-3 w-full">
                <div className="flex items-center gap-2 bg-blue-50 text-blue-700 rounded-lg px-3 py-1.5 text-xs font-semibold flex-1 min-w-0">
                  <span className="material-symbols-outlined text-sm">person</span>
                  <span className="truncate">{transfer.senderEmail} shared files with you</span>
                </div>
                {(() => {
                  const expLabel = formatExpiry(transfer.expiresAt);
                  if (!expLabel) return null;
                  const isExpired = expLabel === 'Expired';
                  const isUrgent = !isExpired && (expLabel.includes('hour') || expLabel.includes('minute'));
                  return (
                    <div className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold shrink-0 ${isExpired ? 'bg-red-50 text-red-600' : isUrgent ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
                      <span className="material-symbols-outlined text-sm">{isExpired ? 'error' : 'schedule'}</span>
                      <span>{expLabel}</span>
                    </div>
                  );
                })()}
              </div>
            )}
            <h1 className="font-serif text-xl md:text-3xl font-medium text-gray-900 mb-1">Files shared with you</h1>
            <p className="text-slate-500 text-xs md:text-sm font-medium">Securely download files sent via Wesaves</p>
          </div>

        {loading && (
          <div className="space-y-3 animate-pulse">
            <div className="h-8 bg-slate-100 rounded-lg w-3/4" />
            <div className="bg-slate-50 rounded-xl border border-slate-100 overflow-hidden">
              <div className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-100">
                <div className="size-4 rounded bg-slate-200" />
                <div className="h-3 bg-slate-200 rounded w-1/3" />
                <div className="h-3 bg-slate-200 rounded w-16 ml-auto" />
              </div>
              {[1, 2, 3].map(i => (
                <div key={i} className="flex items-center gap-3 px-3 py-2.5 border-b border-slate-100 last:border-0">
                  <div className="size-4 rounded bg-slate-100" />
                  <div className="size-7 rounded bg-slate-100" />
                  <div className="h-3 bg-slate-100 rounded flex-1" />
                  <div className="h-3 bg-slate-100 rounded w-14" />
                </div>
              ))}
            </div>
            <div className="h-10 bg-slate-100 rounded-xl w-full" />
          </div>
        )}

        {/* Password gate */}
        {!loading && passwordRequired && (
          <form onSubmit={handlePasswordSubmit} className="space-y-3">
            <div className="flex items-center gap-2 text-slate-700 mb-1">
              <span className="material-symbols-outlined text-primary text-lg">lock</span>
              <span className="text-sm font-semibold">This transfer is password protected</span>
            </div>
            <input
              type="password"
              placeholder="Enter password"
              value={passwordInput}
              onChange={e => setPasswordInput(e.target.value)}
              className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              autoFocus
            />
            {passwordError && <p className="text-red-500 text-xs font-semibold">{passwordError}</p>}
            <button
              type="submit"
              disabled={passwordChecking || !passwordInput.trim()}
              className="w-full py-2.5 rounded-xl bg-primary text-white text-sm font-bold hover:bg-primary/90 transition-colors disabled:opacity-50"
            >
              {passwordChecking ? 'Checking…' : 'Unlock'}
            </button>
          </form>
        )}

        {error && !loading && !passwordRequired && (
          <div className="bg-red-50 border border-red-100 text-red-600 rounded-xl p-4 text-sm font-semibold text-center">
            {error}
          </div>
        )}

        {!loading && !error && !passwordRequired && limitReached && (
          <div className="bg-amber-50 border border-amber-200 text-amber-700 rounded-xl p-4 text-sm font-semibold text-center">
            <span className="material-symbols-outlined text-lg align-middle mr-1">block</span>
            Download limit reached — the sender has restricted the number of downloads for this transfer.
          </div>
        )}

        {!loading && !error && !passwordRequired && !limitReached && (
          <>
            {transfer?.message && (
              <div className="bg-blue-50 border border-blue-100 text-slate-700 rounded-xl px-2.5 py-2 text-xs font-semibold mb-3 md:mb-4">
                {transfer.message}
              </div>
            )}
            <div className="bg-slate-50 rounded-xl border border-slate-100 mb-4 md:mb-5 overflow-hidden">
              {/* Column headers — sortable (OneDrive-style) */}
              <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 bg-slate-50">
                {/* Select-all checkbox */}
                <button
                  onClick={toggleAll}
                  className={`size-4 rounded flex items-center justify-center border transition-colors shrink-0 ${allSelected ? 'bg-primary border-primary' : 'border-slate-300 bg-white hover:border-primary'}`}
                  aria-label="Select all"
                >
                  {allSelected && <span className="material-symbols-outlined text-white text-[11px]">check</span>}
                  {!allSelected && selectedIds.size > 0 && <span className="material-symbols-outlined text-slate-400 text-[11px]">remove</span>}
                </button>
                <button
                  onClick={() => toggleSort('name')}
                  className="flex items-center gap-0.5 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 flex-1"
                >
                  Name
                  {sortBy === 'name' && <span className="material-symbols-outlined text-[11px]">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}
                </button>
                <button
                  onClick={() => toggleSort('size')}
                  className="flex items-center gap-0.5 text-[9px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 shrink-0"
                >
                  Size
                  {sortBy === 'size' && <span className="material-symbols-outlined text-[11px]">{sortDir === 'asc' ? 'arrow_upward' : 'arrow_downward'}</span>}
                </button>
                <div className="w-7 shrink-0" />
              </div>

              <div className="divide-y divide-slate-100">
                {sortedFiles.map((file) => {
                  const isSelected = selectedIds.has(file.id);
                  return (
                    <div
                      key={file.id}
                      onClick={() => toggleFile(file.id)}
                      className={`flex items-center gap-2 bg-white px-3 py-2.5 cursor-pointer transition-colors ${isSelected ? 'bg-blue-50/60' : 'hover:bg-slate-50'}`}
                    >
                      {/* Checkbox */}
                      <div
                        className={`size-4 rounded flex items-center justify-center border transition-colors shrink-0 ${isSelected ? 'bg-primary border-primary' : 'border-slate-300'}`}
                      >
                        {isSelected && <span className="material-symbols-outlined text-white text-[11px]">check</span>}
                      </div>

                      {/* File icon / thumb */}
                      <div className="size-8 rounded-lg overflow-hidden bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                        {fileIcon(file) === 'image' ? (
                          <img
                            src={`/api/transfers/thumb?token=${encodeURIComponent(id || '')}&fileId=${encodeURIComponent(file.id)}`}
                            alt=""
                            className="w-full h-full object-cover"
                            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                          />
                        ) : (
                          <span className="material-symbols-outlined text-sm">{fileIcon(file)}</span>
                        )}
                      </div>

                      {/* Name + size */}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs md:text-sm font-bold text-slate-900 truncate">{file.name}</p>
                      </div>
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest shrink-0">{formatBytes(file.sizeBytes)}</span>

                      {/* Per-file download — stop propagation so it doesn't toggle checkbox */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          void downloadTransferFile(file).catch((err: any) => {
                            showToast(String(err?.message || 'Failed to download file'), 'error');
                          });
                        }}
                        className="p-1 text-slate-300 hover:text-primary transition-colors shrink-0"
                        aria-label={`Download ${file.name}`}
                      >
                        <span className="material-symbols-outlined text-base">download</span>
                      </button>
                    </div>
                  );
                })}
              </div>

              {/* Selection summary bar */}
              {selectedIds.size > 0 && (
                <div className="flex items-center justify-between px-3 py-2 bg-primary/5 border-t border-primary/10">
                  <span className="text-[10px] font-black uppercase tracking-widest text-primary">
                    {selectedIds.size} of {files.length} selected
                  </span>
                  <button
                    onClick={() => setSelectedIds(new Set())}
                    className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-600"
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {isDownloading ? (
              <div className="space-y-4">
                <div className="h-4 w-full bg-slate-100 rounded-full overflow-hidden border border-slate-200">
                  <div
                    className="h-full bg-primary transition-all duration-300 ease-out"
                    style={{ width: `${downloadProgress}%` }}
                  ></div>
                </div>
                <div className="flex justify-between text-xs font-black uppercase text-slate-400 tracking-widest">
                  <span>Downloading…</span>
                  <span>{downloadProgress}%</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {/* Primary action row: Download + Copy Link */}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleDownload()}
                    disabled={isDownloading}
                    className="flex-1 bg-primary hover:bg-blue-600 text-white font-black h-12 rounded-xl shadow-lg shadow-blue-500/20 transition-all transform active:scale-[0.98] flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <span className="material-symbols-outlined text-xl font-bold">download</span>
                    <span>
                      {selectedIds.size > 0
                        ? `Download ${selectedIds.size} File${selectedIds.size !== 1 ? 's' : ''}`
                        : 'Download All'}
                    </span>
                  </button>
                  <button
                    onClick={handleCopyLink}
                    className="h-12 px-4 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 font-bold text-sm transition-all flex items-center gap-2 shrink-0"
                  >
                    <span className="material-symbols-outlined text-base">
                      {linkCopied ? 'check' : 'link'}
                    </span>
                    <span className="hidden sm:inline">{linkCopied ? 'Copied!' : 'Copy Link'}</span>
                  </button>
                </div>

                {isLoggedIn ? (
                  <button
                    onClick={handleSaveToDrive}
                    disabled={isSaving || saveSuccess}
                    className={`w-full h-12 rounded-xl transition-all flex items-center justify-center gap-2 text-sm font-bold ${
                      saveSuccess 
                        ? 'bg-green-50 text-green-600' 
                        : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {isSaving ? (
                      <>
                        <div className="size-4 border-2 border-slate-300 border-t-primary rounded-full animate-spin"></div>
                        <span>Saving to My Drive...</span>
                      </>
                    ) : saveSuccess ? (
                      <>
                        <span className="material-symbols-outlined text-xl">check_circle</span>
                        <span>Saved to My Drive!</span>
                      </>
                    ) : (
                      <>
                        <span className="material-symbols-outlined text-xl">cloud_upload</span>
                        <span>Save to My Drive</span>
                      </>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/auth/signup')}
                    className="w-full bg-slate-100 hover:bg-slate-200 text-slate-600 font-bold h-12 rounded-xl transition-all flex items-center justify-center text-sm"
                  >
                    Save in my Wesaves
                  </button>
                )}
              </div>
            )}

            {transfer?.message && (
              <div className="mt-4 text-center text-xs font-bold text-slate-400 uppercase tracking-widest">
                Message included
              </div>
            )}
          </>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 text-[10px] font-black uppercase text-slate-400 tracking-widest opacity-60">
          <span className="material-symbols-outlined text-sm">verified_user</span>
          Scanned by Wesaves Antivirus
        </div>
      </div>

      <p className="mt-4 text-xs font-bold text-slate-400 uppercase tracking-widest text-center md:text-left">Powered by Wesaves Cloud Technology</p>
      </div>
    </div>
  );
};

export default Download;
